// Client injection for @em/api.
//
// The service layer is transport-agnostic: it does NOT import an Apollo client
// singleton. Instead each app (the SPA, admin, or an SSR site) builds its own
// client — with its own env, auth headers, links — and injects it once at
// startup via `configureApiService`. `ApiService` reads the injected client at
// call time, so it works the same in a browser or on a server.

/** The minimal GraphQL client surface `ApiService` needs — structurally
 *  satisfied by an Apollo `ApolloClient`, but not tied to it. */
export interface ApiClient {
  query(options: any): Promise<any>;
  mutate(options: any): Promise<any>;
  resetStore?(): Promise<any> | void;
}

export interface ApiServiceConfig {
  /** The GraphQL client every operation runs through. */
  client: ApiClient;
  /** App-specific session teardown (e.g. clear localStorage). Optional. */
  onClearSession?: () => void;
  /** Base URL of the REST API, for services that upload files over multipart
   *  (e.g. UploadedFileService) instead of GraphQL. Optional. */
  restApiUrl?: string;
  /** White-label space id sent as the `x-space-id` header on REST uploads.
   *  Optional. */
  spaceId?: string;
}

let currentClient: ApiClient | null = null;
let currentOnClearSession: (() => void) | undefined;
let currentRestApiUrl: string | undefined;
let currentSpaceId: string | undefined;

/** Register the GraphQL client (and optional session teardown + REST upload
 *  env). Call once at app startup, before any query/mutation runs. */
export const configureApiService = (config: ApiServiceConfig): void => {
  currentClient = config.client;
  currentOnClearSession = config.onClearSession;
  currentRestApiUrl = config.restApiUrl;
  currentSpaceId = config.spaceId;
};

/** The configured client. Throws a clear error if `configureApiService` was
 *  never called — a misconfiguration, not a runtime data error. */
export const getApiClient = (): ApiClient => {
  if (!currentClient) {
    throw new Error(
      "[@em/api] No client configured. Call configureApiService({ client }) at app startup before using ApiService."
    );
  }

  return currentClient;
};

export const getOnClearSession = (): (() => void) | undefined => currentOnClearSession;

/** Base URL for REST uploads. Throws if a REST service is used before it was
 *  injected — a misconfiguration, not a runtime data error. */
export const getRestApiUrl = (): string => {
  if (!currentRestApiUrl) {
    throw new Error(
      "[@em/api] No restApiUrl configured. Pass restApiUrl to configureApiService({ ... }) at app startup before using REST upload services."
    );
  }

  return currentRestApiUrl;
};

/** White-label space id for the `x-space-id` REST header (empty string if not set). */
export const getSpaceId = (): string => currentSpaceId ?? "";
